import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { setAccessToken, clearAccessToken, refreshAccessToken } from '../utils/api';

// Create context
const AuthContext = createContext(null);

// Provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount: attempt to restore session via refresh token cookie
  useEffect(() => {
    let cancelled = false;

    const restoreSession = async () => {
      try {
        // Call refresh directly — returns user + accessToken
        const res = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          if (!cancelled && data.accessToken) {
            setAccessToken(data.accessToken);
            setUser(data.user);
          }
        }
      } catch (err) {
        // No valid session — that's fine
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    restoreSession();

    return () => { cancelled = true; };
  }, []);

  // Listen for forced logout from api.js (refresh failure)
  useEffect(() => {
    const handleLogout = () => {
      setUser(null);
      clearAccessToken();
    };
    window.addEventListener('auth:logout', handleLogout);
    return () => window.removeEventListener('auth:logout', handleLogout);
  }, []);

  // Login
  const login = useCallback(async (email, password) => {
    const data = await api('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  // Register
  const register = useCallback(async ({ username, email, password, name, phone }) => {
    const data = await api('/auth/register', {
      method: 'POST',
      body: { username, email, password, name, phone },
    });
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  // Logout
  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch (err) {
      // Ignore errors on logout
    }
    clearAccessToken();
    setUser(null);
  }, []);

  // Update profile
  const updateProfile = useCallback(async (data) => {
    const result = await api('/auth/me', {
      method: 'PUT',
      body: data,
    });
    setUser(result.user);
    return result.user;
  }, []);

  // Change password
  const changePassword = useCallback(async (currentPassword, newPassword) => {
    await api('/auth/change-password', {
      method: 'PUT',
      body: { currentPassword, newPassword },
    });
  }, []);

  const isAuthenticated = !!user;
  const isAdmin = user?.role === 'admin';

  const value = {
    user,
    loading,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    register,
    updateProfile,
    changePassword,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use Auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
